// Scheduled exports page.
//
// Standalone home for automated delivery, split out of the export builder. You
// pick a template (provided or saved) to deliver, then set the cadence (capped
// at once a month) and recipients via ExportSchedulePanel. Existing schedules
// are listed for pause/remove. Decoupling from a live build means a schedule
// always references a named template, which is also what a server-side cron
// would resolve at send time.
//
// Postgres mapping: see exportData.js `user_exports_schedule`; the chosen
// template id is stored alongside the resolved title/format so the cron can
// re-resolve current data at each send.
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Snackbar } from '@mui/material';
import MainCard from '../../../components/cards/MainCard';
import useFeature from '../useFeature';
import {
    PROVIDED_TEMPLATES, SECTION_INDEX, LARGE_TABLES, nextSendAfter,
} from '../exportData';
import {
    useGetExportTemplatesQuery, useGetExportSchedulesQuery, useSaveExportSchedulesMutation,
} from '../../../store/services/reportLayout.service';
import ExportSchedulePanel from './ExportSchedulePanel';
import { TickRow } from './ExportBuilderPage';

export default function ScheduledExportsPage() {
    const dwmOn = useFeature('darkWebMonitoring');
    const tenancyId = useSelector((state) => state.tenancy.tenancyId);
    // Saved templates come from the server-side per-tenancy store, same list
    // the export builder manages.
    const templatesQ = useGetExportTemplatesQuery(tenancyId, { skip: tenancyId == null });
    const templates = useMemo(
        () => [...PROVIDED_TEMPLATES, ...(templatesQ.data?.templates ?? []).map((t) => ({ ...t, custom: true }))],
        [templatesQ.data],
    );
    // null = no document, just the ticked data tables. A schedule that delivers
    // a CSV extract and nothing else is a normal thing to want, and the template
    // row previously had no way to express it - one was always selected.
    const NO_DOC = null;
    const [templateId, setTemplateId] = useState(templates[0]?.id);
    // Full data tables to attach to the delivery as one CSV file each.
    const [tableIds, setTableIds] = useState(() => new Set());
    // Schedules live server-side per tenancy (SK = SCHEDULES). They used to sit
    // in localStorage, which meant they were invisible to anything but the
    // browser that created them - no use to a delivery job, and colleagues in
    // the same tenancy couldn't see what was already going out.
    const schedulesQ = useGetExportSchedulesQuery(tenancyId, { skip: tenancyId == null });
    const [saveExportSchedules] = useSaveExportSchedulesMutation();
    const schedules = schedulesQ.data?.schedules ?? [];
    const [toast, setToast] = useState('');

    // Every mutation PUTs the whole list, matching how templates persist.
    const commit = async (next, message) => {
        try {
            await saveExportSchedules({ tenancyId, schedules: next }).unwrap();
            if (message) setToast(message);
        } catch (e) {
            setToast(e?.data?.message || 'Could not save the schedule - please try again');
        }
    };
    const availableTables = LARGE_TABLES.filter((t) => !t.feature || (t.feature === 'darkWebMonitoring' ? dwmOn : true));
    const toggleTable = (id) => setTableIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // No fallback to the first template: templateId === null is a deliberate
    // choice, not an unset value.
    const selected = templateId === NO_DOC ? null : (templates.find((t) => t.id === templateId) || templates[0]);
    // Count only sections the tenancy can actually render (DWM off hides those).
    const sectionCount = selected
        ? selected.sections.filter((id) => {
            const m = SECTION_INDEX[id];
            return m && !(m.feature === 'darkWebMonitoring' && !dwmOn);
        }).length
        : 0;
    const title = selected ? (selected.title || selected.name) : 'Data tables';
    // Tables go out as CSV, so a tables-only schedule is a CSV delivery.
    const format = selected ? (selected.format || 'PDF') : 'CSV';

    const onSave = (entry) => {
        const item = {
            ...entry,
            id: `sch-${Date.now()}-${schedules.length + 1}`,
            enabled: true,
            templateId: selected?.id ?? null,
            tables: [...tableIds],
            nextSend: nextSendAfter(entry.firstSend, entry.cadence),
        };
        commit(
            [...schedules, item],
            `Scheduled ${entry.cadence} delivery to ${entry.recipients.length} recipient${entry.recipients.length === 1 ? '' : 's'}`,
        );
    };
    const onToggle = (id) => commit(schedules.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));
    const onDelete = (id) => commit(schedules.filter((x) => x.id !== id), 'Schedule removed');

    return (
        <Box sx={{ p: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Scheduled exports</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    Pick a template and have it delivered automatically. Exports go out a maximum of once a month.
                </Typography>
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>What to send</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Choose the template this schedule delivers, plus any full data tables to attach as CSV files - or send data tables on their own.</Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Button
                        size="small" onClick={() => setTemplateId(NO_DOC)}
                        sx={{
                            textTransform: 'none', fontSize: 11.5, px: 1.5, py: '5px', borderRadius: '6px',
                            border: '1px solid', borderColor: templateId === NO_DOC ? 'secondary.main' : 'divider',
                            bgcolor: templateId === NO_DOC ? 'action.selected' : 'transparent',
                            color: templateId === NO_DOC ? 'secondary.main' : 'text.secondary',
                            '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                        }}
                    >
                        Data tables only
                    </Button>
                    {templates.map((t) => {
                        const active = t.id === templateId;
                        return (
                            <Button
                                key={t.id} size="small" onClick={() => setTemplateId(t.id)}
                                sx={{
                                    textTransform: 'none', fontSize: 11.5, px: 1.5, py: '5px', borderRadius: '6px',
                                    border: '1px solid', borderColor: active ? 'secondary.main' : 'divider',
                                    bgcolor: active ? 'action.selected' : 'transparent',
                                    color: active ? 'secondary.main' : 'text.secondary',
                                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                                }}
                            >
                                {t.name}{t.custom ? ' (saved)' : ''}
                            </Button>
                        );
                    })}
                </Box>
                <Box sx={{ px: 3, pb: 1.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>
                        Attach full data tables (CSV)
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.25, maxWidth: 640 }}>
                        {availableTables.map((t) => (
                            <TickRow key={t.id} checked={tableIds.has(t.id)} onToggle={() => toggleTable(t.id)} label={`${t.label} (${t.source})`} />
                        ))}
                    </Box>
                </Box>
                <Box sx={{ px: 3, pb: 2 }}>
                    <Typography sx={{ fontSize: 10.5, color: selected || tableIds.size > 0 ? 'text.secondary' : 'warning.main' }}>
                        {selected ? (
                            <>
                                Sends <strong>{title}</strong> · {format} · {sectionCount} section{sectionCount === 1 ? '' : 's'}
                                {tableIds.size > 0 ? ` · ${tableIds.size} data table${tableIds.size === 1 ? '' : 's'} attached as CSV` : ''}
                            </>
                        ) : tableIds.size > 0 ? (
                            <>Sends <strong>{tableIds.size} data table{tableIds.size === 1 ? '' : 's'}</strong> as CSV · no document</>
                        ) : (
                            'Pick a template, or tick at least one data table to send on its own.'
                        )}
                    </Typography>
                </Box>
            </MainCard>

            <ExportSchedulePanel
                title={title} format={format} sectionCount={sectionCount} tableCount={tableIds.size}
                schedules={schedules} onSave={onSave} onToggle={onToggle} onDelete={onDelete}
            />

            <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
